import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  FileText,
  Loader2,
  MessageCircle,
  Plus,
  Braces,
  Save,
  Send,
  Trash2,
} from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import { useAuth } from '../contexts/AuthContext'
import {
  MARKETING_TEMPLATE_CHANNELS,
  VARIABLES_DE_CAMPANA,
  WHATSAPP_LARGO_RECOMENDADO,
  type MarketingTemplateChannel,
  type MarketingTemplateDto,
  type MarketingTemplateListResponse,
  type TestSendMarketingTemplateResponse,
} from '@jr2os/shared'

/**
 * Plantillas de marketing: la lista a la izquierda, el editor a la derecha.
 *
 * Dos columnas y no tres: las plantillas de documento tienen una tercera con el
 * catálogo de variables porque son veintiséis. Aquí son dos, y caben en una
 * línea de ayuda debajo del cuerpo.
 */
export default function MarketingTemplates() {
  const { t } = useTranslation(['marketing', 'common'])
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const canWrite = role === 'ADMIN' || role === 'CONSULTOR' || role === 'ANALISTA'

  const [items, setItems] = useState<MarketingTemplateDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [seleccion, setSeleccion] = useState<string | 'nueva' | null>(null)
  const [busy, setBusy] = useState(false)
  const cuerpoRef = useRef<HTMLTextAreaElement>(null)
  const [form, setForm] = useState({
    name: '',
    channel: 'CORREO' as MarketingTemplateChannel,
    subject: '',
    bodyText: '',
  })

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<MarketingTemplateListResponse>('/marketing/templates')
      setItems(r.items)
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, t('templates.errors.load')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void reload()
  }, [reload])

  const elegir = (p: MarketingTemplateDto) => {
    setSeleccion(p.id)
    setForm({
      name: p.name,
      channel: p.channel,
      subject: p.subject ?? '',
      bodyText: p.bodyText,
    })
  }

  const nueva = () => {
    setSeleccion('nueva')
    setForm({ name: '', channel: 'CORREO', subject: '', bodyText: '' })
  }

  /**
   * Mete la variable DONDE ESTÁ EL CURSOR, no al final.
   *
   * Copiar al portapapeles —que es lo que hace el editor de plantillas de
   * documento— obliga a un segundo gesto y a acordarse de dónde ibas. Aquí se
   * escribe en el sitio y el cursor se queda detrás, listo para seguir.
   */
  const insertar = (clave: string) => {
    const marca = `{{${clave}}}`
    const el = cuerpoRef.current
    if (!el) {
      setForm((f) => ({ ...f, bodyText: f.bodyText + marca }))
      return
    }
    const ini = el.selectionStart ?? el.value.length
    const fin = el.selectionEnd ?? ini
    const nuevo = el.value.slice(0, ini) + marca + el.value.slice(fin)
    setForm((f) => ({ ...f, bodyText: nuevo }))
    // El cursor detrás de lo insertado, en cuanto React haya repintado.
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(ini + marca.length, ini + marca.length)
    })
  }

  const guardar = async () => {
    setBusy(true)
    try {
      const cuerpo = {
        name: form.name,
        channel: form.channel,
        subject: form.channel === 'CORREO' ? form.subject : null,
        bodyText: form.bodyText,
      }
      if (seleccion === 'nueva') {
        const r = await api.post<{ template: MarketingTemplateDto }>(
          '/marketing/templates',
          cuerpo,
        )
        setSeleccion(r.template.id)
      } else {
        await api.patch(`/marketing/templates/${seleccion}`, cuerpo)
      }
      toast.success(t('templates.saved'))
      await reload()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('templates.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const borrar = async (p: MarketingTemplateDto) => {
    if (!(await confirmDialog({ message: t('templates.confirmDelete', { name: p.name }), tone: 'danger' })))
      return
    try {
      await api.delete(`/marketing/templates/${p.id}`)
      if (seleccion === p.id) setSeleccion(null)
      await reload()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('templates.errors.generic')))
    }
  }

  const probar = async () => {
    setBusy(true)
    try {
      const r = await api.post<TestSendMarketingTemplateResponse>(
        `/marketing/templates/${seleccion}/test-send`,
        {},
      )
      toast.success(t('templates.testSent', { email: r.enviadoA }))
    } catch (err) {
      toast.error(apiErrorMessage(err, t('templates.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-jr2-500/50'
  const puedeGuardar =
    form.name.trim() && form.bodyText.trim() && (form.channel !== 'CORREO' || form.subject.trim())
  const largoWhatsApp = form.channel === 'WHATSAPP' && form.bodyText.length > WHATSAPP_LARGO_RECOMENDADO

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <Link to="/marketing" className="text-xs t-muted hover:t-primary inline-flex items-center gap-1">
            <ArrowLeft size={12} /> {t('templates.backToHub')}
          </Link>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2 mt-1">
            <FileText size={20} className="text-fuchsia-600 dark:text-fuchsia-300" />
            {t('templates.title')}
          </h1>
          <p className="text-xs t-muted max-w-2xl">{t('templates.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={nueva}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('templates.newButton')}
          </button>
        )}
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="grid md:grid-cols-[260px_1fr] gap-4 items-start">
        <div className="space-y-4">
        <aside className="surface overflow-hidden">
          {loading && (
            <p className="px-3 py-6 text-center t-muted text-sm">
              <Loader2 size={14} className="inline-block animate-spin" /> {t('common:loading')}
            </p>
          )}
          {!loading && items.length === 0 && (
            <p className="px-3 py-8 text-center t-muted text-sm">{t('templates.empty')}</p>
          )}
          <ul>
            {items.map((p) => (
              <li key={p.id} className="border-b border-base/40 last:border-0">
                <div
                  className={`flex items-center gap-2 px-3 py-2 ${
                    seleccion === p.id ? 'bg-jr2-500/10' : 'hover:bg-hover'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => elegir(p)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="block text-sm t-primary truncate">{p.name}</span>
                    <span className="block text-xs t-faint inline-flex items-center gap-1">
                      {p.channel === 'WHATSAPP' ? (
                        <>
                          <MessageCircle size={10} /> WhatsApp
                        </>
                      ) : (
                        <>
                          <Send size={10} /> {t('templates.channel.CORREO')}
                        </>
                      )}
                    </span>
                  </button>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => void borrar(p)}
                      title={t('common:actions.delete')}
                      className="shrink-0 p-1.5 rounded t-faint hover:bg-hover hover:text-rose-600"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </aside>

        {/* El catálogo de lo que se puede meter en el texto. Es LA MISMA lista
            que usa el motor al enviar (`VARIABLES_DE_CAMPANA` en el paquete
            compartido), así que aquí no puede aparecer nada que luego salga
            literal en el correo del cliente. */}
        <aside className="surface p-4 space-y-3">
          <h3 className="text-sm font-semibold t-primary flex items-center gap-2">
            <Braces size={14} className="text-fuchsia-600 dark:text-fuchsia-300" />
            {t('templates.vars.title')}
          </h3>
          <p className="text-xs t-muted">{t('templates.vars.hint')}</p>
          <ul className="space-y-1.5">
            {VARIABLES_DE_CAMPANA.map((v) => (
              <li key={v.clave}>
                <button
                  type="button"
                  disabled={!canWrite || seleccion === null}
                  onClick={() => insertar(v.clave)}
                  title={t('templates.vars.insert')}
                  className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-hover disabled:opacity-40 disabled:hover:bg-transparent transition"
                >
                  <code className="text-xs font-mono text-fuchsia-700 dark:text-fuchsia-200">
                    {`{{${v.clave}}}`}
                  </code>
                  <span className="block text-xs t-secondary leading-snug">{v.descripcion}</span>
                  <span className="block text-[11px] t-faint">
                    {t('templates.vars.fallback', { valor: v.siNoHay })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="text-[11px] t-faint">{t('templates.vars.footer')}</p>
        </aside>
        </div>

        <section className="surface p-4 space-y-3">
          {seleccion === null ? (
            <p className="t-muted text-sm py-10 text-center">{t('templates.pickOne')}</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="space-y-1.5 block">
                  <span className="text-sm font-medium t-primary">{t('templates.form.name')}</span>
                  <input
                    value={form.name}
                    disabled={!canWrite}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                  />
                </label>
                <label className="space-y-1.5 block">
                  <span className="text-sm font-medium t-primary">{t('templates.form.channel')}</span>
                  <select
                    value={form.channel}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setForm({ ...form, channel: e.target.value as MarketingTemplateChannel })
                    }
                    className={inputCls}
                  >
                    {MARKETING_TEMPLATE_CHANNELS.map((c) => (
                      <option key={c} value={c}>
                        {t(`templates.channel.${c}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {form.channel === 'CORREO' ? (
                <label className="space-y-1.5 block">
                  <span className="text-sm font-medium t-primary">{t('templates.form.subject')}</span>
                  <input
                    value={form.subject}
                    disabled={!canWrite}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className={inputCls}
                  />
                </label>
              ) : (
                /* No se finge un canal que no existe: aquí no se envía nada. */
                <p className="text-xs rounded-lg border border-base bg-[var(--bg-input-soft)] px-3 py-2 t-secondary">
                  {t('templates.whatsappHint')}
                </p>
              )}

              <label className="space-y-1.5 block">
                <span className="text-sm font-medium t-primary">{t('templates.form.body')}</span>
                <textarea
                  ref={cuerpoRef}
                  value={form.bodyText}
                  disabled={!canWrite}
                  onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
                  rows={12}
                  className={inputCls}
                />
                <span className="text-xs t-faint block">{t('templates.form.bodyHint')}</span>
                {largoWhatsApp && (
                  <span className="text-xs text-amber-700 dark:text-amber-300 block">
                    {t('templates.whatsappTooLong', {
                      n: form.bodyText.length,
                      max: WHATSAPP_LARGO_RECOMENDADO,
                    })}
                  </span>
                )}
              </label>

              {form.channel === 'CORREO' && (
                <p className="text-xs t-faint">{t('templates.footerHint')}</p>
              )}

              {canWrite && (
                <div className="flex items-center justify-end gap-2 pt-1">
                  {seleccion !== 'nueva' && form.channel === 'CORREO' && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void probar()}
                      className="px-3 py-2 rounded-lg text-sm t-secondary border border-base hover:bg-hover inline-flex items-center gap-2 disabled:opacity-50"
                      title={t('templates.testSendHint')}
                    >
                      <Send size={14} /> {t('templates.testSend')}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy || !puedeGuardar}
                    onClick={() => void guardar()}
                    className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {t('common:actions.save')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}
